import { useEffect, useState } from "react";
import {
  getExchangeCatalog,
  getOrderProductVariants,
  getProductBySlug,
} from "../../api/productVariants";

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_SEARCH = {
  products: [],
  credit: null,
  currency: "",
  nextCursor: null,
};

async function loadCatalog(shipInsureId, merchantId) {
  const { data: orderedVariants = [] } =
    await getOrderProductVariants(shipInsureId);
  const handles = [
    ...new Set(
      orderedVariants.map((variant) => variant.product_handle).filter(Boolean),
    ),
  ];
  const products = await Promise.all(
    handles.map((handle) =>
      getProductBySlug(merchantId, handle).then((response) => response.data),
    ),
  );

  const byProduct = {};
  products.forEach((product) => {
    byProduct[String(product.id)] = product.variants.map((variant) => ({
      ...variant,
      variant_id: variant.id,
    }));
  });
  return byProduct;
}

export function useExchangeCatalog(order) {
  const [catalog, setCatalog] = useState({});
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (
      !order?.allow_exchanges ||
      !order?.shipInsureID ||
      !order?.merchant_id
    ) {
      setCatalog({});
      setStatus("idle");
      return undefined;
    }

    let cancelled = false;
    setStatus("loading");
    loadCatalog(order.shipInsureID, order.merchant_id)
      .then((byProduct) => {
        if (cancelled) return;
        setCatalog(byProduct);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setCatalog({});
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [order?.allow_exchanges, order?.shipInsureID, order?.merchant_id]);

  return { catalog, status };
}

const fetchExchangePage = async (shipInsureId, lineItemId, query, cursor) => {
  const { data } = await getExchangeCatalog(shipInsureId, {
    line_item_id: lineItemId,
    query,
    ...(cursor ? { cursor } : {}),
  });
  return data;
};

const toSearchResult = (data, previousProducts) => ({
  products: [...previousProducts, ...data.products],
  credit: data.credit,
  currency: data.currency,
  nextCursor: data.next_cursor,
});

export const useExchangeProductSearch = (shipInsureId, lineItemId, query) => {
  const [result, setResult] = useState(EMPTY_SEARCH);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (!shipInsureId || !lineItemId) return undefined;

    let cancelled = false;
    setStatus("loading");
    const timer = setTimeout(() => {
      fetchExchangePage(shipInsureId, lineItemId, query)
        .then((data) => {
          if (cancelled) return;
          setResult(toSearchResult(data, []));
          setStatus("ready");
        })
        .catch(() => {
          if (!cancelled) setStatus("error");
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shipInsureId, lineItemId, query]);

  const loadMore = () => {
    setStatus("loading");
    fetchExchangePage(shipInsureId, lineItemId, query, result.nextCursor)
      .then((data) => {
        setResult((previous) => toSearchResult(data, previous.products));
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  };

  return { ...result, status, loadMore };
};
